import React, { ComponentProps, useState } from "react";
import { ButtonLink, Icon } from "@clarkinc/zest-design-system/ui";
import { cls } from "cls";
import AddToCartWithQuantity from "./components/AddToCartWithQuantity";
import AddToCart from "./components/AddToCart";
import StandardPrice from "./components/StandardPrice";
import { IResolvedPricing } from "~/features/pricing/pricingResolver";
import QuantitySelect from "~/features/shopping/ui/QuantitySelect";
import { User } from "~/common/Store/models/User";
import { checkQuantityValidity } from "~/features/shopping/utility/quantities";
interface PricingProps extends ComponentProps<"div"> {
  minBuy: number;
  mustBuy: number;
  showPrice: boolean;
  showQuantitySelect: boolean;
  resolvedPricing: IResolvedPricing;
  customizationUrl: string;
  atcSuccess: boolean;
  setAtcSuccess: React.Dispatch<React.SetStateAction<boolean>>;
  requestAQuoteEnabled: boolean;
  requiresAccessories: boolean;
  quantity: string;
  onQuantityChange: (quantity: string) => void;
  onAddToCart: () => void;
  user: User;
}
const Pricing = ({
  minBuy,
  mustBuy,
  showPrice,
  showQuantitySelect,
  resolvedPricing,
  customizationUrl,
  atcSuccess,
  setAtcSuccess,
  requestAQuoteEnabled,
  requiresAccessories,
  quantity,
  onQuantityChange,
  onAddToCart,
  user
}: PricingProps) => {
  const [error, setError] = useState<string>();
  const [timer, setTimer] = useState(0);
  const handleMouseEnter = () => {
    window.clearTimeout(timer);
    setTimer(0);
  };
  const handleMouseLeave = () => {
    setTimer(window.setTimeout(() => {
      setAtcSuccess(false);
    }, 10000));
  };
  const handleAddToCart = () => {
    setError(undefined);
    const [valid, reason] = checkQuantityValidity(Number(quantity), minBuy, mustBuy);
    if (!valid) {
      setError(reason!);
      return;
    }
    onAddToCart();
  };
  if (requestAQuoteEnabled) return <ButtonLink variant="primary" color="red" className="whitespace-nowrap" href="/special-orders">
        Request a Quote
      </ButtonLink>;
  let additionalText = "";
  if (resolvedPricing.metadata?.requiresCustomization) {
    additionalText = "Starting At";
    if (resolvedPricing.metadata?.googleView && user.registered) additionalText = "From";
  }
  if (user.registered && resolvedPricing.metadata?.belowMapQualified && resolvedPricing.metadata?.mapStyle !== "G") {
    if (user.hasWebPlus) additionalText = "Plus Member Price";else if (user.platinum?.enabled) additionalText = "Platinum Member Price";else additionalText = `Custom Quote for ${user.companyName || user.fullName}`;
  }
  const renderCart = () => {
    if (resolvedPricing.metadata?.requiresCustomization && (resolvedPricing.metadata.customizable || customizationUrl)) {
      return <ButtonLink variant="primary" color="red" href={customizationUrl}>
          Customize
        </ButtonLink>;
    }
    if (requiresAccessories) {
      return <ButtonLink variant="primary" color="red" href="#priceBox">
          Configure
        </ButtonLink>;
    }
    if (showQuantitySelect) {
      return <>
          <QuantitySelect aria-label="quantity select" className="w-32" inputSize="large" labelProps={{
          className: "m-0"
        }} label="" minimumBuy={minBuy} mustBuy={mustBuy} value={quantity} onValueChange={onQuantityChange} />
          <AddToCart className="whitespace-nowrap" onClick={handleAddToCart} error={error}>
            Add to Cart
          </AddToCart>
        </>;
    }
    return <AddToCartWithQuantity error={error} size="large" inputProps={{
      label: "",
      className: "w-10",
      value: quantity,
      errorProps: {
        errorMessages: error
      },
      onValueChange: onQuantityChange
    }} buttonProps={{
      "aria-label": "",
      className: "whitespace-nowrap w-[120px]",
      onClick: handleAddToCart
    }} data-sentry-element="AddToCartWithQuantity" data-sentry-component="renderCart" data-sentry-source-file="index.tsx">
        Add to Cart
      </AddToCartWithQuantity>;
  };
  return <div className="w-auto transition-all duration-200" data-sentry-component="Pricing" data-sentry-source-file="index.tsx">
      <div className={cls("flex gap-2 align-middle", atcSuccess && "hidden")}>
        {showPrice && <StandardPrice price={resolvedPricing.memberPricing ?? resolvedPricing.price} unitOfMeasure={resolvedPricing.metadata?.googleView && !resolvedPricing.metadata?.requiresCustomization && !user.isLoggedIn && resolvedPricing.unitOfMeasure || ""} extraText={additionalText} />}
        {renderCart()}
      </div>
      <div className={cls("hidden items-center", atcSuccess && "flex gap-2")} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <div className="w-16 text-[11px] font-semibold leading-[11px] text-center max-w-21">
          <span>{`${quantity} item${Number(quantity) > 1 ? "s" : ""}`}</span>
          {" added to your cart"}
        </div>
        <ButtonLink variant="primary" color="green" href="/viewcart.cfm" className="whitespace-nowrap" data-sentry-element="ButtonLink" data-sentry-source-file="index.tsx">
          <Icon name="ri-shopping-cart-2-fill" className="fill-white w-3 h-3 pr-1" data-sentry-element="Icon" data-sentry-source-file="index.tsx" />
          View Cart
        </ButtonLink>
        <ButtonLink variant="primary" color="orange" href="/shipping-billinginfo.cfm" className="btn btn-orange" data-sentry-element="ButtonLink" data-sentry-source-file="index.tsx">
          <Icon name="ri-lock-fill" className="fill-white w-3 h-3 pr-1" data-sentry-element="Icon" data-sentry-source-file="index.tsx" />
          Checkout
        </ButtonLink>
      </div>
    </div>;
};
export default Pricing;